import React from "react";
import { Text, View } from "@react-pdf/renderer";

type Segment = {
  text: string;
  bold: boolean;
  italic: boolean;
  color?: string;
  fontSize?: number;
};

const STYLE_OPEN = "@@STYLE_OPEN@@";
const STYLE_CLOSE = "@@STYLE_CLOSE@@";

/**
 * Stateful left-to-right scanner.
 * Tracks bold/italic state via toggles and color/fontSize via stacks (for nested <style> tags).
 * Each marker (** or @@STYLE_OPEN@@...@@) flushes the current buffer and updates state.
 */
const parseSegments = (text: string): Segment[] => {
  const segments: Segment[] = [];
  let pos = 0;
  let bold = false;
  let italic = false;
  const colorStack: (string | undefined)[] = [];
  const sizeStack: (number | undefined)[] = [];
  let buffer = "";

  const currentColor = (): string | undefined => {
    for (let i = colorStack.length - 1; i >= 0; i--) {
      if (colorStack[i]) return colorStack[i];
    }
    return undefined;
  };

  const currentSize = (): number | undefined => {
    for (let i = sizeStack.length - 1; i >= 0; i--) {
      if (sizeStack[i]) return sizeStack[i];
    }
    return undefined;
  };

  const flush = () => {
    if (buffer.length > 0) {
      segments.push({
        text: buffer,
        bold,
        italic,
        color: currentColor(),
        fontSize: currentSize(),
      });
      buffer = "";
    }
  };

  while (pos < text.length) {
    // @@STYLE_OPEN@@size=18,color=#BA0C2F@@
    if (text.startsWith(STYLE_OPEN, pos)) {
      const endIdx = text.indexOf("@@", pos + STYLE_OPEN.length);
      if (endIdx !== -1) {
        flush();
        const attrs = text.slice(pos + STYLE_OPEN.length, endIdx);
        const size = attrs.match(/size=([^,]+)/)?.[1];
        const color = attrs.match(/color=([^,]+)/)?.[1];
        colorStack.push(color || undefined);
        sizeStack.push(size ? Number(size) : undefined);
        pos = endIdx + 2;
        continue;
      }
    }

    if (text.startsWith(STYLE_CLOSE, pos)) {
      flush();
      colorStack.pop();
      sizeStack.pop();
      pos += STYLE_CLOSE.length;
      continue;
    }

    if (text.startsWith("***", pos)) {
      flush();
      bold = !bold;
      italic = !italic;
      pos += 3;
      continue;
    }

    if (text.startsWith("**", pos)) {
      flush();
      bold = !bold;
      pos += 2;
      continue;
    }

    if (text[pos] === "*") {
      flush();
      italic = !italic;
      pos += 1;
      continue;
    }

    if (text.startsWith("___", pos)) {
      flush();
      bold = !bold;
      italic = !italic;
      pos += 3;
      continue;
    }

    if (text.startsWith("__", pos)) {
      flush();
      bold = !bold;
      pos += 2;
      continue;
    }

    if (text[pos] === "_") {
      flush();
      italic = !italic;
      pos += 1;
      continue;
    }

    buffer += text[pos];
    pos++;
  }

  flush();
  return segments;
};

/**
 * Converts a markdown string to react-pdf Text / View nodes.
 *
 * Supported syntax:
 *   **bold**  __bold__
 *   *italic*  _italic_
 *   ***bi***  ___bi___
 *   <br>  → line break
 *   ---   → horizontal rule
 *   <style size="18" color="red">text</style> → custom style (nestable)
 *   <color style="#HEX">text</color>          → legacy color tag
 */
const parseMarkdownForPDF = (text: string, style: Record<string, any> = {}) => {
  if (!text || typeof text !== "string") return null;

  const normalised = text
    .replace(/<br\s*\/?>/gi, "\n")
    // Convert <style size="X" color="Y"> → marker. Captures only size/color attrs.
    .replace(/<style\s+([^>]*)>/gi, (_m, attrs: string) => {
      const size = attrs.match(/size\s*=\s*"([^"]+)"/i)?.[1] || "";
      const color = attrs.match(/color\s*=\s*"([^"]+)"/i)?.[1] || "";
      const parts: string[] = [];
      if (size) parts.push(`size=${String(Number(size) - 4)}`);
      if (color) parts.push(`color=${color}`);
      return `${STYLE_OPEN}${parts.join(",")}@@`;
    })
    .replace(/<\/style>/gi, STYLE_CLOSE)
    // Legacy <color style="#HEX"> → same marker system
    .replace(/<color\s+style="([^"]+)">/gi, (_m, hex: string) => `${STYLE_OPEN}color=${hex}@@`)
    .replace(/<\/color>/gi, STYLE_CLOSE)
    // Strip remaining HTML
    .replace(/<[^>]*>/g, "")
    .replace(/\p{Emoji_Presentation}/gu, "")
    .replace(/\p{Emoji}\uFE0F/gu, "");

  const lines = normalised.split("\n");

  return (
    <>
      {lines.map((line, lineIndex) => {
        if (!line.trim()) {
          return (
            <Text key={lineIndex} style={{ height: 8 }}>
              {" "}
            </Text>
          );
        }

        if (line.trim() === "---") {
          return (
            <View key={lineIndex} style={{ borderBottomWidth: 1, borderBottomColor: "#e5e7eb", marginVertical: 6 }} />
          );
        }

        const segments = parseSegments(line);

        if (segments.length === 0) {
          return (
            <Text key={lineIndex} style={{ marginBottom: 4, ...style }}>
              {line}
            </Text>
          );
        }

        return (
          <Text key={lineIndex} style={{ marginBottom: 4, ...style }}>
            {segments.map((seg, segIndex) => (
              <Text
                key={segIndex}
                style={{
                  fontWeight: seg.bold ? "bold" : "normal",
                  fontStyle: seg.italic ? "italic" : "normal",
                  ...(seg.color ? { color: seg.color } : {}),
                  ...(seg.fontSize ? { fontSize: seg.fontSize } : {}),
                }}
              >
                {seg.text}
              </Text>
            ))}
          </Text>
        );
      })}
    </>
  );
};

export default parseMarkdownForPDF;
